/**
 * useTrendingTokens Hook (v4)
 *
 * Fetches trending tokens with scores calculated from token_stats table.
 *
 * v4 Algorithm (volume-dominant):
 * - Volume Score (60%): sqrt-scaled 24h volume
 * - Trade Count Score (20%): Number of trades (organic activity indicator)
 * - Trader Score (10%): Unique traders (distribution indicator)
 * - Recency Score (10%): Slight freshness bonus
 */

import { useQuery, keepPreviousData } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';
import { MeteoraDBCClient } from '@/lib/meteora/dbcClient';
import { getSolanaEndpoint } from '@/lib/solana/config';
import { GRADUATION_THRESHOLD_SOL } from '@/lib/meteora/graduation';
import { getSOLPrice } from '@/hooks/useSOLPrice';
import { getLockedCreatorFeesUsd } from '@/lib/solUsdConversion';
import { fetchPoolSnapshot } from '@/lib/tradingview/api';

// Debug logging - only enabled in development
const DEBUG = import.meta.env.DEV;

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_ANON_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

export interface TrendingToken {
  id: string;
  mintAddress: string;
  poolAddress: string;
  name: string;
  symbol: string;
  imageUrl: string | null;
  description: string | null;
  creatorAddress: string;
  creatorUsername: string | null;
  creatorAvatarUrl: string | null;
  creatorAvatarPath: string | null;
  creatorAvatarVersion: number;
  createdAt: Date;
  // Price data (fetched from pool)
  currentPrice: number | null;
  fdv: number | null;
  fdvUsd: number | null;
  priceChange5m: number | null;
  priceChange1h: number | null;
  priceChange24h: number | null;
  volume24h: number | null;
  volumeTotal: number | null;
  /**
   * Historically locked creator fees USD (read from pool_stats by sync-pool-stats).
   * This value MUST NOT be recomputed by multiplying a SOL amount by the live
   * SOL price. It is populated by getLockedCreatorFeesUsd from the two columns
   * below. See src/lib/solUsdConversion.ts for the rationale.
   */
  creatorFeesUsd: number | null;
  /** Raw locked USD from pool_stats.cumulative_creator_earnings_usd (bonding curve). */
  cumulativeCreatorEarningsUsd: number | null;
  /** Raw locked USD from pool_stats.damm_v2_cumulative_fee_usd (post graduation). */
  dammV2CumulativeFeeUsd: number | null;
  // Bonding curve progress
  reserveQuote: number | null;
  bondingProgress: number;
  // Graduation status
  isMigrated: boolean;
  // Activity stats from Helius
  tradeCount24h: number;
  holderCount: number; // uniqueTraders
  // Sorting score
  trendingScore: number;
}

interface UseTrendingTokensOptions {
  limit?: number;
}

interface TrendingApiToken {
  tokenMint: string;
  poolAddress: string;
  name: string;
  symbol: string;
  imageUrl: string | null;
  description: string | null;
  creatorAddress: string;
  createdAt: string;
  volume24h: number;
  volumeTotal?: number;
  tradeCount24h: number;
  uniqueTraders: number;
  trendingScore: number;
}

export type TrendingWindow = '24h' | '7d' | 'empty';

interface TrendingApiResponse {
  tokens: TrendingApiToken[];
  source: 'token_stats' | 'rpc';
  window?: TrendingWindow;
  calculatedAt: string;
}

interface TrendingTokensResult {
  tokens: TrendingToken[];
  window: TrendingWindow;
}

// (POOL_BATCH_SIZE removed — now using getPoolInfoBatch for single-RPC lookups)

/**
 * Fetch trending tokens from edge function (token_stats based).
 * Returns the API tokens plus the selection window so the caller can
 * relabel the surface (e.g. "Active this week" instead of "Trending coins").
 */
async function fetchTrendingFromApi(
  limit: number
): Promise<{ tokens: TrendingApiToken[]; window: TrendingWindow }> {
  if (!SUPABASE_URL) {
    console.warn('[useTrendingTokens] Missing SUPABASE_URL');
    return { tokens: [], window: 'empty' };
  }

  try {
    const url = `${SUPABASE_URL}/functions/v1/trending-tokens?limit=${limit}`;

    const response = await fetch(url, {
      method: 'GET',
      headers: {
        'Content-Type': 'application/json',
        'Authorization': `Bearer ${SUPABASE_ANON_KEY}`,
        'apikey': SUPABASE_ANON_KEY,
      },
    });

    if (!response.ok) {
      console.error('[useTrendingTokens] API error:', response.status);
      return { tokens: [], window: 'empty' };
    }

    const data: TrendingApiResponse = await response.json();
    DEBUG && console.log(`[useTrendingTokens] API returned ${data.tokens.length} tokens (window=${data.window ?? 'unknown'})`);

    // Default to '24h' for backward compat with old edge-function responses
    // that don't yet include the field. Defaulting to 'empty' would
    // incorrectly hide the carousel during a brief deploy skew.
    return { tokens: data.tokens, window: data.window ?? '24h' };
  } catch (error) {
    console.error('[useTrendingTokens] Failed to fetch trending:', error);
    return { tokens: [], window: 'empty' };
  }
}

/**
 * Enrich trending tokens with pool snapshot data (no RPC calls on happy path)
 */
async function fetchTrendingTokens(limit: number): Promise<TrendingTokensResult> {
  // Step 1: Get trending scores, pool snapshot, and on-chain reserves in parallel
  const [apiResult, snapshot, solPrice, poolStatsResult] = await Promise.all([
    fetchTrendingFromApi(limit * 2),
    fetchPoolSnapshot(),
    getSOLPrice().catch(() => 200),
    // pool_stats has accurate on-chain quote_reserve (lamports) synced by sync-pool-stats.
    // The market-data snapshot's quoteReserve is unreliable (never updated from on-chain).
    // We also read the historically locked creator fees USD columns so the frontend
    // can render them without re-multiplying by a live SOL price. See
    // src/lib/solUsdConversion.ts for the locked vs live contract.
    supabase
      .from('pool_stats')
      .select('pool_address, quote_reserve, cumulative_creator_earnings_usd, damm_v2_cumulative_fee_usd')
      .then(r => r.data ?? []),
  ]);

  // Build lookup: pool_address -> quote_reserve in SOL
  const reserveByPool = new Map<string, number>();
  // Build lookup: pool_address -> locked creator fees USD snapshots
  const lockedFeesByPool = new Map<string, { cumulativeCreatorEarningsUsd: number | null; dammV2CumulativeFeeUsd: number | null }>();
  for (const row of poolStatsResult) {
    if (row.quote_reserve != null) {
      reserveByPool.set(row.pool_address, Number(row.quote_reserve) / 1e9);
    }
    lockedFeesByPool.set(row.pool_address, {
      cumulativeCreatorEarningsUsd: row.cumulative_creator_earnings_usd != null ? Number(row.cumulative_creator_earnings_usd) : null,
      dammV2CumulativeFeeUsd: row.damm_v2_cumulative_fee_usd != null ? Number(row.damm_v2_cumulative_fee_usd) : null,
    });
  }

  const apiTokens = apiResult.tokens;

  if (apiTokens.length === 0) {
    // Empty trending list: surface the empty/empty-window state directly.
    // The carousel hides itself on tokens.length === 0 (see TrendingCarousel.tsx).
    return { tokens: [], window: apiResult.window };
  }

  // Step 2: Fetch creator profiles and migration status in parallel
  const uniqueCreators = [...new Set(apiTokens.map(t => t.creatorAddress))];
  const [profilesResult, migratedResult] = await Promise.all([
    supabase
      .from('creator_profiles')
      .select('wallet_address, username, avatar_url, avatar_path, avatar_version')
      .in('wallet_address', uniqueCreators),
    supabase
      .from('launched_tokens')
      .select('mint_address, is_migrated')
      .in('mint_address', apiTokens.map(t => t.tokenMint)),
  ]);

  const profileMap = new Map<string, { username: string | null; avatar_url: string | null; avatar_path: string | null; avatar_version: number }>();
  if (profilesResult.data) {
    for (const p of profilesResult.data) {
      profileMap.set(p.wallet_address, p);
    }
  }

  const migratedMints = new Set(
    (migratedResult.data ?? []).filter(r => r.is_migrated).map(r => r.mint_address)
  );

  // Step 3: Map tokens using snapshot data
  const totalSupply = 1_000_000_000;
  const trendingTokens: TrendingToken[] = [];
  const missingMints: string[] = [];

  for (const token of apiTokens) {
    if (trendingTokens.length >= limit) break;

    const snap = snapshot[token.poolAddress];
    const profile = profileMap.get(token.creatorAddress);

    if (snap) {
      // Use snapshot data (fast path)
      const currentPrice = snap.price;
      // Prefer on-chain reserves from pool_stats (accurate), fall back to snapshot
      const reserveQuote = reserveByPool.get(token.poolAddress) ?? snap.quoteReserve ?? 0;
      const isMigrated = migratedMints.has(token.tokenMint);
      const rawBondingProgress = Math.min((reserveQuote / GRADUATION_THRESHOLD_SOL) * 100, 100);
      const bondingProgress = isMigrated ? 100 : rawBondingProgress;
      const fdv = currentPrice * totalSupply;
      const locked = lockedFeesByPool.get(token.poolAddress);
      const cumulativeCreatorEarningsUsd = locked?.cumulativeCreatorEarningsUsd ?? null;
      const dammV2CumulativeFeeUsd = locked?.dammV2CumulativeFeeUsd ?? null;
      const creatorFeesUsd = getLockedCreatorFeesUsd({
        cumulativeCreatorEarningsUsd,
        dammV2CumulativeFeeUsd,
        isGraduated: isMigrated || bondingProgress >= 100,
      });

      trendingTokens.push({
        id: token.tokenMint,
        mintAddress: token.tokenMint,
        poolAddress: token.poolAddress,
        name: token.name,
        symbol: token.symbol,
        imageUrl: token.imageUrl,
        description: token.description ?? null,
        creatorAddress: token.creatorAddress,
        creatorUsername: profile?.username ?? null,
        creatorAvatarUrl: profile?.avatar_url ?? null,
        creatorAvatarPath: profile?.avatar_path ?? null,
        creatorAvatarVersion: profile?.avatar_version ?? 0,
        createdAt: new Date(token.createdAt),
        currentPrice,
        fdv,
        fdvUsd: fdv * solPrice,
        priceChange5m: snap.priceChange5m,
        priceChange1h: snap.priceChange1h,
        priceChange24h: snap.priceChange24h,
        volume24h: token.volume24h,
        volumeTotal: token.volumeTotal ?? null,
        creatorFeesUsd,
        cumulativeCreatorEarningsUsd,
        dammV2CumulativeFeeUsd,
        reserveQuote,
        bondingProgress,
        isMigrated,
        tradeCount24h: token.tradeCount24h,
        holderCount: token.uniqueTraders,
        trendingScore: token.trendingScore,
      });
    } else {
      missingMints.push(token.tokenMint);
    }
  }

  // Step 4: RPC fallback for tokens missing from snapshot
  if (missingMints.length > 0 && trendingTokens.length < limit) {
    DEBUG && console.log(`[useTrendingTokens] RPC fallback for ${missingMints.length} missing tokens`);
    try {
      const endpoint = getSolanaEndpoint();
      const client = new MeteoraDBCClient(endpoint);
      const poolInfoMap = await client.getPoolInfoBatch(missingMints);

      for (const token of apiTokens) {
        if (trendingTokens.length >= limit) break;
        if (!missingMints.includes(token.tokenMint)) continue;

        const poolInfo = poolInfoMap.get(token.tokenMint);
        if (!poolInfo) continue;

        const reserveQuote = poolInfo.reserveQuote ?? 0;
        const isMigrated = migratedMints.has(token.tokenMint);
        const rawBondingProgress = Math.min((reserveQuote / GRADUATION_THRESHOLD_SOL) * 100, 100);
        const bondingProgress = isMigrated ? 100 : rawBondingProgress;
        const fdv = poolInfo.currentPrice !== null ? poolInfo.currentPrice * totalSupply : null;
        const profile = profileMap.get(token.creatorAddress);
        const locked = lockedFeesByPool.get(token.poolAddress);
        const cumulativeCreatorEarningsUsd = locked?.cumulativeCreatorEarningsUsd ?? null;
        const dammV2CumulativeFeeUsd = locked?.dammV2CumulativeFeeUsd ?? null;
        const creatorFeesUsd = getLockedCreatorFeesUsd({
          cumulativeCreatorEarningsUsd,
          dammV2CumulativeFeeUsd,
          isGraduated: isMigrated || bondingProgress >= 100,
        });

        trendingTokens.push({
          id: token.tokenMint,
          mintAddress: token.tokenMint,
          poolAddress: token.poolAddress,
          name: token.name,
          symbol: token.symbol,
          imageUrl: token.imageUrl,
          description: token.description ?? null,
          creatorAddress: token.creatorAddress,
          creatorUsername: profile?.username ?? null,
          creatorAvatarUrl: profile?.avatar_url ?? null,
          creatorAvatarPath: profile?.avatar_path ?? null,
          creatorAvatarVersion: profile?.avatar_version ?? 0,
          createdAt: new Date(token.createdAt),
          currentPrice: poolInfo.currentPrice,
          fdv,
          fdvUsd: fdv != null ? fdv * solPrice : null,
          priceChange5m: null,
          priceChange1h: null,
          priceChange24h: poolInfo.priceChange24h,
          volume24h: token.volume24h,
          volumeTotal: token.volumeTotal ?? null,
          creatorFeesUsd,
          cumulativeCreatorEarningsUsd,
          dammV2CumulativeFeeUsd,
          reserveQuote,
          bondingProgress,
          isMigrated,
          tradeCount24h: token.tradeCount24h,
          holderCount: token.uniqueTraders,
          trendingScore: token.trendingScore,
        });
      }
      // Include tokens where RPC returned no pool data
      for (const token of apiTokens) {
        if (trendingTokens.length >= limit) break;
        if (!missingMints.includes(token.tokenMint)) continue;
        if (trendingTokens.some(t => t.mintAddress === token.tokenMint)) continue;
        const isMigrated = migratedMints.has(token.tokenMint);
        const profile = profileMap.get(token.creatorAddress);
        const locked = lockedFeesByPool.get(token.poolAddress);
        const cumulativeCreatorEarningsUsd = locked?.cumulativeCreatorEarningsUsd ?? null;
        const dammV2CumulativeFeeUsd = locked?.dammV2CumulativeFeeUsd ?? null;
        const creatorFeesUsd = getLockedCreatorFeesUsd({
          cumulativeCreatorEarningsUsd,
          dammV2CumulativeFeeUsd,
          isGraduated: isMigrated,
        });

        trendingTokens.push({
          id: token.tokenMint,
          mintAddress: token.tokenMint,
          poolAddress: token.poolAddress,
          name: token.name,
          symbol: token.symbol,
          imageUrl: token.imageUrl,
          description: token.description ?? null,
          creatorAddress: token.creatorAddress,
          creatorUsername: profile?.username ?? null,
          creatorAvatarUrl: profile?.avatar_url ?? null,
          creatorAvatarPath: profile?.avatar_path ?? null,
          creatorAvatarVersion: profile?.avatar_version ?? 0,
          createdAt: new Date(token.createdAt),
          currentPrice: null,
          fdv: null,
          fdvUsd: null,
          priceChange5m: null,
          priceChange1h: null,
          priceChange24h: null,
          volume24h: token.volume24h,
          volumeTotal: token.volumeTotal ?? null,
          creatorFeesUsd,
          cumulativeCreatorEarningsUsd,
          dammV2CumulativeFeeUsd,
          reserveQuote: null,
          bondingProgress: isMigrated ? 100 : 0,
          isMigrated,
          tradeCount24h: token.tradeCount24h,
          holderCount: token.uniqueTraders,
          trendingScore: token.trendingScore,
        });
      }
    } catch (err) {
      DEBUG && console.warn('[useTrendingTokens] RPC fallback failed:', err);
      // Still include missing tokens with null price data so they appear in the carousel
      for (const token of apiTokens) {
        if (trendingTokens.length >= limit) break;
        if (!missingMints.includes(token.tokenMint)) continue;
        const isMigrated = migratedMints.has(token.tokenMint);
        const profile = profileMap.get(token.creatorAddress);
        const locked = lockedFeesByPool.get(token.poolAddress);
        const cumulativeCreatorEarningsUsd = locked?.cumulativeCreatorEarningsUsd ?? null;
        const dammV2CumulativeFeeUsd = locked?.dammV2CumulativeFeeUsd ?? null;
        const creatorFeesUsd = getLockedCreatorFeesUsd({
          cumulativeCreatorEarningsUsd,
          dammV2CumulativeFeeUsd,
          isGraduated: isMigrated,
        });

        trendingTokens.push({
          id: token.tokenMint,
          mintAddress: token.tokenMint,
          poolAddress: token.poolAddress,
          name: token.name,
          symbol: token.symbol,
          imageUrl: token.imageUrl,
          description: token.description ?? null,
          creatorAddress: token.creatorAddress,
          creatorUsername: profile?.username ?? null,
          creatorAvatarUrl: profile?.avatar_url ?? null,
          creatorAvatarPath: profile?.avatar_path ?? null,
          creatorAvatarVersion: profile?.avatar_version ?? 0,
          createdAt: new Date(token.createdAt),
          currentPrice: null,
          fdv: null,
          fdvUsd: null,
          priceChange5m: null,
          priceChange1h: null,
          priceChange24h: null,
          volume24h: token.volume24h,
          volumeTotal: token.volumeTotal ?? null,
          creatorFeesUsd,
          cumulativeCreatorEarningsUsd,
          dammV2CumulativeFeeUsd,
          reserveQuote: null,
          bondingProgress: isMigrated ? 100 : 0,
          isMigrated,
          tradeCount24h: token.tradeCount24h,
          holderCount: token.uniqueTraders,
          trendingScore: token.trendingScore,
        });
      }
    }
  }

  DEBUG && console.log(`[useTrendingTokens] Returning ${trendingTokens.length} trending tokens (window=${apiResult.window})`);

  return { tokens: trendingTokens, window: apiResult.window };
}

export function useTrendingTokens(options: UseTrendingTokensOptions = {}) {
  const { limit = 20 } = options;

  const query = useQuery({
    queryKey: ['trending-tokens', limit],
    queryFn: () => fetchTrendingTokens(limit),
    staleTime: 15 * 1000,
    gcTime: 5 * 60 * 1000,
    refetchInterval: 30 * 1000,
    placeholderData: keepPreviousData,
    refetchOnWindowFocus: false, // Disabled to prevent thundering herd on tab focus
    refetchIntervalInBackground: false,
  });

  return {
    tokens: query.data?.tokens ?? [],
    window: query.data?.window ?? 'empty' as TrendingWindow,
    isLoading: query.isLoading,
    isFetching: query.isFetching,
    isError: query.isError,
    error: query.error,
    refetch: query.refetch,
  };
}
